import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Car, Home as HomeIcon, Calendar as CalendarIcon, MapPin, Clock, CheckCircle2, ArrowRight, ArrowLeft, Loader2, User, Mail, Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    service_id: searchParams.get('service') || '',
    service_name: '',
    date: null,
    time: '',
    address: '',
    city: '',
    postal_code: '',
    notes: '',
    client_name: '',
    client_email: '',
    client_phone: '',
  });

  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'
  ];

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (formData.service_id && services.length > 0) {
      const service = services.find(s => s.id === formData.service_id);
      if (service) {
        setFormData(prev => ({ ...prev, service_name: service.name }));
      }
    }
  }, [formData.service_id, services]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Erreur lors du chargement des services');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const bookingData = {
        ...formData,
        date: format(formData.date, 'yyyy-MM-dd'),
      };
      
      await axios.post(`${API}/bookings/guest`, bookingData);
      toast.success('Réservation confirmée ! Vous recevrez un email de confirmation.');
      navigate('/');
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.service_id !== '';
      case 2:
        return formData.date !== null && formData.time !== '';
      case 3:
        return formData.address !== '' && formData.city !== '' && formData.postal_code !== '' &&
               formData.client_name !== '' && formData.client_email !== '' && formData.client_phone !== '';
      default:
        return true;
    }
  };

  const selectedService = services.find(s => s.id === formData.service_id);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-slate-50" data-testid="booking-page">
      {/* Header */}
      <section className="bg-slate-900 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mb-4">
            Réserver un service
          </h1>
          <p className="text-slate-400 text-lg">
            Suivez les étapes pour réserver votre prestation
          </p>
        </div>
      </section>

      {/* Wizard Steps */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Service' },
              { num: 2, label: 'Date & Heure' },
              { num: 3, label: 'Vos infos' },
              { num: 4, label: 'Confirmation' },
            ].map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div 
                    className={`wizard-step ${
                      step > s.num ? 'completed' : step === s.num ? 'active' : 'inactive'
                    }`}
                    data-testid={`step-${s.num}`}
                  >
                    {step > s.num ? <CheckCircle2 className="w-5 h-5" /> : s.num}
                  </div>
                  <span className={`mt-2 text-xs sm:text-sm font-medium ${
                    step >= s.num ? 'text-slate-900' : 'text-slate-400'
                  }`}>
                    {s.label}
                  </span>
                </div>
                {i < 3 && (
                  <div className={`h-0.5 w-8 sm:w-12 md:w-24 mx-2 ${
                    step > s.num ? 'bg-green-500' : 'bg-slate-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card className="border-none shadow-lg mb-8">
          <CardContent className="p-8">
            {/* Step 1: Service Selection */}
            {step === 1 && (
              <div className="animate-fade-in" data-testid="step-1-content">
                <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-6">
                  Choisissez votre service
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {services.map((service) => {
                    const Icon = service.category === 'automobile' ? Car : HomeIcon;
                    return (
                      <button
                        key={service.id}
                        onClick={() => setFormData({ ...formData, service_id: service.id, service_name: service.name })}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          formData.service_id === service.id
                            ? 'border-sky-500 bg-sky-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`select-service-${service.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            formData.service_id === service.id ? 'bg-sky-500' : 'bg-slate-100'
                          }`}>
                            <Icon className={`w-6 h-6 ${
                              formData.service_id === service.id ? 'text-white' : 'text-slate-600'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-900 mb-1">{service.name}</h3>
                            <p className="text-slate-500 text-sm mb-2 line-clamp-2">{service.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-sky-600 font-semibold">{service.price_info}</span>
                              {service.duration && (
                                <span className="text-slate-400 text-sm flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {service.duration}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 2: Date & Time */}
            {step === 2 && (
              <div className="animate-fade-in" data-testid="step-2-content">
                <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-6">
                  Choisissez la date et l'heure
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <Label className="mb-3 block text-slate-700 font-medium">Date</Label>
                    <Calendar
                      mode="single"
                      selected={formData.date}
                      onSelect={(date) => setFormData({ ...formData, date })}
                      locale={fr}
                      disabled={(date) => date < new Date() || date.getDay() === 0}
                      className="rounded-xl border border-slate-200 p-3"
                      data-testid="booking-calendar"
                    />
                  </div>
                  <div>
                    <Label className="mb-3 block text-slate-700 font-medium">Heure</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {timeSlots.map((time) => (
                        <button
                          key={time}
                          onClick={() => setFormData({ ...formData, time })}
                          className={`p-4 rounded-xl border-2 font-medium transition-all ${
                            formData.time === time
                              ? 'border-sky-500 bg-sky-50 text-sky-600'
                              : 'border-slate-200 hover:border-slate-300 text-slate-700'
                          }`}
                          data-testid={`time-slot-${time.replace(':', '')}`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Client Info & Address */}
            {step === 3 && (
              <div className="animate-fade-in" data-testid="step-3-content">
                <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-6">
                  Vos coordonnées
                </h2>
                <div className="space-y-6">
                  {/* Client Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="client_name" className="text-slate-700 font-medium flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Nom complet *
                      </Label>
                      <Input
                        id="client_name"
                        placeholder="Jean Dupont"
                        value={formData.client_name}
                        onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                        className="mt-2 h-12 rounded-lg border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                        data-testid="input-client-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="client_phone" className="text-slate-700 font-medium flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        Téléphone *
                      </Label>
                      <Input
                        id="client_phone"
                        type="tel"
                        placeholder="06 00 00 00 00"
                        value={formData.client_phone}
                        onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                        className="mt-2 h-12 rounded-lg border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                        data-testid="input-client-phone"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="client_email" className="text-slate-700 font-medium flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email *
                    </Label>
                    <Input
                      id="client_email"
                      type="email"
                      placeholder="votre@email.com"
                      value={formData.client_email}
                      onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                      className="mt-2 h-12 rounded-lg border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                      data-testid="input-client-email"
                    />
                  </div>

                  <hr className="border-slate-200" />

                  <h3 className="font-['Outfit'] text-xl font-semibold text-slate-900">
                    Adresse d'intervention
                  </h3>

                  <div>
                    <Label htmlFor="address" className="text-slate-700 font-medium">Adresse *</Label>
                    <Input
                      id="address"
                      placeholder="Numéro et nom de rue"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="mt-2 h-12 rounded-lg border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                      data-testid="input-address"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="postal_code" className="text-slate-700 font-medium">Code postal *</Label>
                      <Input
                        id="postal_code"
                        placeholder="63600"
                        value={formData.postal_code}
                        onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                        className="mt-2 h-12 rounded-lg border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                        data-testid="input-postal"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city" className="text-slate-700 font-medium">Ville *</Label>
                      <Input
                        id="city"
                        placeholder="Ambert"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="mt-2 h-12 rounded-lg border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                        data-testid="input-city"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="notes" className="text-slate-700 font-medium">Notes supplémentaires (optionnel)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Instructions particulières, code d'accès, etc."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="mt-2 rounded-lg border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                      rows={3}
                      data-testid="input-notes"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Confirmation */}
            {step === 4 && (
              <div className="animate-fade-in" data-testid="step-4-content">
                <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-6">
                  Récapitulatif de votre réservation
                </h2>
                
                <div className="space-y-6">
                  <div className="bg-slate-50 rounded-xl p-6">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-sky-500" />
                      Vos coordonnées
                    </h3>
                    <div className="space-y-2 text-slate-700">
                      <p><strong>Nom :</strong> {formData.client_name}</p>
                      <p><strong>Téléphone :</strong> {formData.client_phone}</p>
                      <p><strong>Email :</strong> {formData.client_email}</p>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-6">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Car className="w-5 h-5 text-sky-500" />
                      Service sélectionné
                    </h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{selectedService?.name}</p>
                      </div>
                      <span className="text-sky-600 font-semibold">{selectedService?.price_info}</span>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-6">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-sky-500" />
                      Date et heure
                    </h3>
                    <p className="text-slate-700">
                      {formData.date && format(formData.date, 'EEEE d MMMM yyyy', { locale: fr })} à {formData.time}
                    </p>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-6">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-sky-500" />
                      Adresse
                    </h3>
                    <p className="text-slate-700">
                      {formData.address}<br />
                      {formData.postal_code} {formData.city}
                    </p>
                    {formData.notes && (
                      <p className="text-slate-500 text-sm mt-2">
                        Note : {formData.notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
              {step > 1 ? (
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  className="rounded-full px-6"
                  data-testid="btn-prev"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Précédent
                </Button>
              ) : (
                <div />
              )}

              {step < 4 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="rounded-full bg-sky-500 hover:bg-sky-600 px-6"
                  data-testid="btn-next"
                >
                  Suivant
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 px-8"
                  data-testid="btn-confirm"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Confirmation...
                    </>
                  ) : (
                    <>
                      Confirmer la réservation
                      <CheckCircle2 className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BookingPage;
